// "Hansı mətbəxə göndərilsin?" seçimi.
//
// Bir restoranda bir neçə hazırlıq nöqtəsi ola bilər (Bar, Əsas Mətbəx,
// Soyuq Mətbəx) və hər birinin öz printeri var. Ofisant "Mətbəxə göndər"
// deyəndə sifariş fişi MƏHZ seçilmiş mətbəxin printerindən çıxmalıdır.
//
// Davranış:
//   • 0 mətbəx  → dialoq açılmır, avtomatik marşrut (köhnə davranış).
//   • 1 mətbəx  → dialoq açılmır, birbaşa həmin mətbəxə gedir.
//   • 2+ mətbəx → seçim göstərilir. "Avtomatik" variantı da var: hər məhsul
//     öz kateqoriyasına bağlı mətbəxə gedir (içki → bar, yemək → mətbəx).
//
// İstifadə:
//   const { pickKitchen, kitchenPickerDialog } = useKitchenPicker();
//   const choice = await pickKitchen();
//   if (choice === null) return;              // ləğv edildi
//   ...add-stock({ kitchen_id: choice.kitchenId })   // null = avtomatik

import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { ChefHat, PrinterIcon, Sparkles, AlertTriangle } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import { getKitchens } from "../api/kitchen";

export interface KitchenOption {
  id: number;
  name: string;
  color?: string | null;
  /** Mətbəxə printer bağlanmayıbsa fiş çıxmayacaq — istifadəçiyə deyirik. */
  has_printer?: boolean;
}

export interface KitchenChoice {
  /** `null` → avtomatik marşrut (kateqoriyaya görə). */
  kitchenId: number | null;
  kitchenName: string | null;
}

/** Nəticə: `null` = ofisant ləğv etdi. */
type Resolver = (value: KitchenChoice | null) => void;

export function useKitchenPicker(): {
  pickKitchen: () => Promise<KitchenChoice | null>;
  kitchenPickerDialog: React.ReactNode;
  /** Yüklənmiş mətbəxlər — çağıran tərəf sayına baxmaq istəsə. */
  kitchens: KitchenOption[];
} {
  const { t } = useI18n();
  const [kitchens, setKitchens] = useState<KitchenOption[]>([]);
  const [open, setOpen] = useState(false);
  const resolverRef = useRef<Resolver | null>(null);
  // Siyahını ref-də də saxlayırıq: `pickKitchen` callback-i state-in köhnə
  // snapshot-una ilişməsin (bir dəfə yüklənir, sonra dəyişmir).
  const kitchensRef = useRef<KitchenOption[]>([]);

  useEffect(() => {
    const controller = new AbortController();
    getKitchens(controller.signal)
      .then((list) => {
        const mapped = (list as KitchenOption[]).filter((k) => k && k.id > 0);
        kitchensRef.current = mapped;
        setKitchens(mapped);
      })
      .catch(() => {
        // Mətbəx siyahısı alınmadı — seçim göstərilmir, avtomatik marşrut işləyir.
        kitchensRef.current = [];
        setKitchens([]);
      });
    return () => controller.abort();
  }, []);

  const settle = useCallback((value: KitchenChoice | null) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setOpen(false);
    resolve?.(value);
  }, []);

  const pickKitchen = useCallback(() => {
    const list = kitchensRef.current;
    // Seçiləcək bir şey yoxdursa dialoq açmırıq — ofisantı boş yerə saxlamaq
    // POS-da ən pis şeydir.
    if (list.length === 0) {
      return Promise.resolve<KitchenChoice | null>({
        kitchenId: null,
        kitchenName: null,
      });
    }
    if (list.length === 1) {
      const only = list[0];
      return Promise.resolve<KitchenChoice | null>({
        kitchenId: only.id,
        kitchenName: only.name,
      });
    }
    resolverRef.current?.(null);
    setOpen(true);
    return new Promise<KitchenChoice | null>((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        settle(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, settle]);

  // Komponent unmount olsa promise asılı qalmasın.
  useEffect(() => () => resolverRef.current?.(null), []);

  const kitchenPickerDialog = (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          onClick={() => settle(null)}
          role="presentation"
        >
          <motion.div
            className="w-full max-w-md rounded-2xl bg-white dark:bg-[#111318] border border-gray-200 dark:border-white/10 shadow-2xl p-6"
            initial={{ scale: 0.94, y: 12, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.96, y: 8, opacity: 0 }}
            transition={{ type: "spring", stiffness: 420, damping: 30, mass: 0.7 }}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="flex flex-col items-center text-center gap-3">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center bg-orange-50 dark:bg-orange-500/10 text-[var(--primary)]">
                <ChefHat className="w-7 h-7" />
              </div>
              <h3 className="text-lg font-black text-gray-900 dark:text-white">
                {t("pos.kitchenPicker.title")}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                {t("pos.kitchenPicker.subtitle")}
              </p>
            </div>

            <div className="mt-5 flex flex-col gap-2 max-h-[45vh] overflow-y-auto">
              <button
                autoFocus
                onClick={() => settle({ kitchenId: null, kitchenName: null })}
                className="w-full flex items-center gap-3 p-3 rounded-xl border border-dashed border-gray-300 dark:border-white/15 hover:border-[var(--primary)] hover:bg-orange-50/60 dark:hover:bg-orange-500/[0.07] transition-colors text-left"
              >
                <span className="w-9 h-9 shrink-0 rounded-lg flex items-center justify-center bg-gray-100 dark:bg-white/[0.06] text-gray-500 dark:text-gray-300">
                  <Sparkles className="w-4 h-4" />
                </span>
                <span className="min-w-0">
                  <span className="block font-bold text-sm text-gray-900 dark:text-white">
                    {t("pos.kitchenPicker.auto")}
                  </span>
                  <span className="block text-xs text-gray-500 dark:text-gray-400">
                    {t("pos.kitchenPicker.autoHint")}
                  </span>
                </span>
              </button>

              {kitchens.map((k) => {
                const missingPrinter = k.has_printer === false;
                return (
                  <button
                    key={k.id}
                    onClick={() => settle({ kitchenId: k.id, kitchenName: k.name })}
                    className="w-full flex items-center gap-3 p-3 rounded-xl border border-gray-200 dark:border-white/10 hover:border-[var(--primary)] hover:bg-orange-50/60 dark:hover:bg-orange-500/[0.07] transition-colors text-left"
                  >
                    <span
                      className="w-9 h-9 shrink-0 rounded-lg flex items-center justify-center text-white"
                      style={{ backgroundColor: k.color || "var(--primary)" }}
                    >
                      <PrinterIcon className="w-4 h-4" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block font-bold text-sm text-gray-900 dark:text-white truncate">
                        {k.name}
                      </span>
                      {missingPrinter && (
                        <span className="flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
                          <AlertTriangle className="w-3 h-3 shrink-0" />
                          {t("pos.kitchenPicker.noPrinter")}
                        </span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => settle(null)}
              className="mt-4 w-full py-3 rounded-xl font-bold text-sm bg-gray-100 dark:bg-white/[0.06] text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-white/[0.1] transition-colors"
            >
              {t("common.cancel")}
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return { pickKitchen, kitchenPickerDialog, kitchens };
}
